/**
 * CheckEmailCard.tsx — wait for the standalone /auth verify-link.
 *
 * WHY: a browser register is emailVerified:false until the 24h link is
 * followed. The patient must not reach the dashboard (or skip checkout
 * account) until that is true. Resend is the recovery path when the mail
 * is missing or expired. Refresh-on-focus + "I've confirmed" cover the
 * other-tab verify (the email app) without polling.
 *
 * ONE CARD FOR EVERY "CONFIRM YOUR EMAIL" (2026-09-26). Three screens wait on
 * the same link: `/auth`'s check-email, the checkout account step's email hold
 * (after payment), and — since orders S0.4 — the checkout's "Confirm your email
 * to continue" hold on the plan save, the session and the attach
 * (`checkout/CheckoutOwnershipHold.tsx`). They share this card, so there is one
 * resend machine and one confirm control, never a second copy of either.
 *
 * THE CHECKOUT-STEP VARIANT (`onContinue`). The confirm button reads
 * "I’ve confirmed it — continue" and hands the press to the caller, which
 * re-reads the session and RETRIES THE STEP it was refused on — a re-read alone
 * would leave the patient looking at the same hold. The caller also supplies the
 * opening line (`lead`), which names the masked address. Without `onContinue`
 * the card is exactly what the other two screens always had: "Resend link" and
 * "I've confirmed", and the confirm only re-reads the session (the account
 * step's email-hold effect does the rest).
 *
 * THE RESEND MACHINE (both variants). identity's `resend-verification` answers
 * 200 whatever the address (no oracle) and sits behind a per-IP + per-email
 * throttle (identity `GuestThrottleGuard`, 10 a minute by default) that answers
 * 429. So:
 *  - after a send, the button rests for {@link RESEND_COOLDOWN_MS}, SAYING so in
 *    visible words tied by `aria-describedby` — a patient pressing it five times
 *    because nothing seemed to happen is exactly what walks into the throttle;
 *  - a 429 says to wait, and never claims a link is on its way;
 *  - any other failure: the account screens keep the "if that email is on file"
 *    line (a delivery fault must not read as "no such account"); the checkout
 *    step, whose address is the signed-in patient's own, says the send did not
 *    happen — telling them a link is coming when none is would leave them waiting.
 * The outcome is announced through a status region that is in the DOM from the
 * first render (one whose text arrives with it is not reliably announced), and
 * both buttons are `aria-disabled` while they work — never `disabled`, which
 * would drop the keyboard to `<body>` mid-press.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { resendVerification } from '../api/identity';
import { Alert, Button, Card, Help } from '../ui/kit';
import { useAuth } from './AuthContext';

/** How long the resend button rests after a send (or a 429) before it can send again. */
export const RESEND_COOLDOWN_MS = 60_000;

export const RESEND_LABEL = 'Resend link';
export const CONFIRMED_LABEL = "I've confirmed";
/** The checkout-step variant's two controls. */
export const RESEND_AGAIN_LABEL = 'Send the link again';
export const CONFIRMED_CONTINUE_LABEL = 'I’ve confirmed it — continue';

/** The account screens' resend outcome — deliberately says nothing about whether the address exists. */
export const RESEND_SENT_ACCOUNT = 'If that email is on file, a new link is on its way.';
/** The checkout step's resend outcome (the signed-in patient's own address). */
export const RESEND_SENT_STEP = 'A new link is on its way. It can take a minute to arrive — check your junk folder too.';
/** The checkout step when the send did not happen (a network fault, a 5xx). */
export const RESEND_FAILED_STEP = 'We couldn’t send a new link just now. Wait a moment, then send the link again.';
/** identity's throttle said no (429), on either variant. */
export const RESEND_RATE_LIMITED = 'You’ve asked for several links in a short time. Please wait a minute before sending another.';
/** Why the resend button is resting, in words, tied to it. */
export const RESEND_COOLDOWN_REASON = 'You can send another link in a minute.';

type ResendOutcome = null | 'sent' | 'failed' | 'rate_limited';

export function CheckEmailCard({
  email,
  lead,
  onContinue,
  continueBusy = false,
}: {
  email?: string;
  /** Replaces the opening line (the checkout step names the masked address). */
  lead?: ReactNode;
  /**
   * THE CHECKOUT-STEP VARIANT: the confirm button becomes
   * {@link CONFIRMED_CONTINUE_LABEL} and calls this. The caller re-reads the
   * session and retries the refused step.
   */
  onContinue?: () => void;
  /** True while the caller's re-read + retry is running. */
  continueBusy?: boolean;
}) {
  const { me, refresh } = useAuth();
  const address = email ?? me?.email;
  const stepVariant = onContinue !== undefined;
  const [outcome, setOutcome] = useState<ResendOutcome>(null);
  const [sending, setSending] = useState(false);
  const [coolingDown, setCoolingDown] = useState(false);
  const [checking, setChecking] = useState(false);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cooldownReasonId = useId();

  useEffect(() => {
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refresh]);

  useEffect(
    () => () => {
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    },
    [],
  );

  function startCooldown() {
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    setCoolingDown(true);
    cooldownTimer.current = setTimeout(() => {
      cooldownTimer.current = null;
      setCoolingDown(false);
    }, RESEND_COOLDOWN_MS);
  }

  async function handleResend() {
    if (sending || coolingDown || !address) return;
    setSending(true);
    try {
      await resendVerification(address);
      setOutcome('sent');
      startCooldown();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setOutcome('rate_limited');
        startCooldown();
      } else {
        // Generic on the account screens — existence must not leak and a
        // delivery fault must not look like failure. The checkout step's
        // address is the signed-in patient's own, so it says the truth.
        setOutcome(stepVariant ? 'failed' : 'sent');
      }
    } finally {
      setSending(false);
    }
  }

  async function handleConfirmed() {
    if (stepVariant) {
      if (!continueBusy) onContinue?.();
      return;
    }
    if (checking) return;
    setChecking(true);
    try {
      await refresh();
    } finally {
      setChecking(false);
    }
  }

  const outcomeText =
    outcome === 'rate_limited'
      ? RESEND_RATE_LIMITED
      : outcome === 'failed'
        ? RESEND_FAILED_STEP
        : outcome === 'sent'
          ? stepVariant
            ? RESEND_SENT_STEP
            : RESEND_SENT_ACCOUNT
          : null;
  const resendResting = sending || coolingDown;
  const confirmBusy = stepVariant ? continueBusy : checking;
  const confirmLabel = stepVariant ? CONFIRMED_CONTINUE_LABEL : CONFIRMED_LABEL;
  const resendButton = (
    <Button
      type="button"
      variant={stepVariant ? 'ghost' : 'primary'}
      aria-disabled={resendResting || undefined}
      aria-describedby={coolingDown ? cooldownReasonId : undefined}
      onClick={() => void handleResend()}
    >
      {sending ? 'Sending…' : stepVariant ? RESEND_AGAIN_LABEL : RESEND_LABEL}
    </Button>
  );
  const confirmButton = (
    <Button
      type="button"
      variant={stepVariant ? 'primary' : 'ghost'}
      aria-disabled={confirmBusy || undefined}
      onClick={() => void handleConfirmed()}
    >
      {confirmBusy ? 'Checking…' : confirmLabel}
    </Button>
  );

  return (
    <Card>
      {lead ?? (
        <Help>
          We&rsquo;ve sent a link to confirm your address. Open it to finish creating your account —
          you can use your dashboard once that&rsquo;s done.
        </Help>
      )}
      {/* In the DOM from the first render and EMPTY until there is something to
          say, so the resend outcome is announced when it arrives. */}
      <div role="status">
        {outcomeText ? (
          <Alert
            tone={outcome === 'sent' ? 'success' : 'info'}
            style={{ marginBottom: 'var(--gps-s-3)' }}
          >
            {outcomeText}
          </Alert>
        ) : null}
      </div>
      <p style={{ margin: '0 0 var(--gps-s-3)', color: 'var(--gps-muted)' }}>
        {stepVariant
          ? 'The link lasts 24 hours. Can’t find it, or has it run out? Check your junk folder, then send the link again.'
          : 'The link expires after 24 hours. Didn’t get it? Check junk mail, then resend.'}
      </p>
      {address ? (
        <>
          <div style={{ display: 'flex', gap: 'var(--gps-s-2)', flexWrap: 'wrap' }}>
            {/* The step variant leads with the way ON; the account screens keep
                their order (resend first), unchanged. */}
            {stepVariant ? (
              <>
                {confirmButton}
                {resendButton}
              </>
            ) : (
              <>
                {resendButton}
                {confirmButton}
              </>
            )}
          </div>
          {coolingDown ? (
            <p id={cooldownReasonId} style={{ margin: 'var(--gps-s-2) 0 0', fontSize: '14px', color: 'var(--gps-muted)' }}>
              {RESEND_COOLDOWN_REASON}
            </p>
          ) : null}
        </>
      ) : stepVariant ? (
        // No address on the session (an older identity `/auth/me`): nothing to
        // resend to, but the way on still works.
        <div style={{ display: 'flex', gap: 'var(--gps-s-2)', flexWrap: 'wrap' }}>{confirmButton}</div>
      ) : (
        <p style={{ margin: 0 }}>
          <Link to="/auth?mode=login" style={{ color: 'var(--gps-link)' }}>
            Sign in
          </Link>{' '}
          to resend the link.
        </p>
      )}
    </Card>
  );
}
